import { usePhoneViewerLayout } from "@/hooks/use-phone-viewer-layout";
import { selectModeName } from "@/store/mode-selectors";
import { useAppSelector } from "@/store/store-hooks";
import {
  createContext,
  useContext,
  useState,
  type JSX,
  type PropsWithChildren,
} from "react";

type MobileSurface =
  | "project"
  | "more"
  | "mode"
  | "view"
  | "tools"
  | "representation";

type MobileViewerUi = {
  surface: MobileSurface | null;
  setSurface(surface: MobileSurface | null): void;
  viewContainer: HTMLDivElement | null;
  setViewContainer(container: HTMLDivElement | null): void;
  representationContainer: HTMLDivElement | null;
  setRepresentationContainer(container: HTMLDivElement | null): void;
};

const MobileViewerUiContext = createContext<MobileViewerUi | undefined>(
  undefined,
);

/** @returns The shared transient phone UI, separate from viewer tool state. */
export function MobileViewerUiProvider({
  children,
}: PropsWithChildren): JSX.Element {
  const isPhone = usePhoneViewerLayout();
  const mode = useAppSelector(selectModeName);
  const [previousLayout, setPreviousLayout] = useState({ isPhone, mode });
  const [surface, setSurface] = useState<MobileSurface | null>(null);
  const [viewContainer, setViewContainer] = useState<HTMLDivElement | null>(
    null,
  );
  const [representationContainer, setRepresentationContainer] =
    useState<HTMLDivElement | null>(null);
  if (previousLayout.isPhone !== isPhone || previousLayout.mode !== mode) {
    setPreviousLayout({ isPhone, mode });
    setSurface(null);
  }
  return (
    <MobileViewerUiContext
      value={{
        surface,
        setSurface,
        viewContainer,
        setViewContainer,
        representationContainer,
        setRepresentationContainer,
      }}
    >
      {children}
    </MobileViewerUiContext>
  );
}

/** @returns The optional mobile controls context; desktop-only consumers need no provider. */
export function useMobileViewerUi(): MobileViewerUi | undefined {
  return useContext(MobileViewerUiContext);
}
